import { StudioSystemType } from 'types/global'
import { PanelBlock } from './types'

export function getGridUuidsFromBlocks(panelBlocks: PanelBlock[]): Set<string> {
  return new Set(panelBlocks.map((block) => block.uuid))
}

export function getSystemFromBlocks(panelBlocks: PanelBlock[]): StudioSystemType | undefined {
  const firstGrid = panelBlocks[0]?.uuid ? window.editor.objectByUuid(panelBlocks[0].uuid) : null
  return firstGrid?.getSystem?.() ?? window.editor.selectedSystem
}

export function getStringCountForBlocks(panelBlocks: PanelBlock[], system: StudioSystemType | undefined): number {
  if (!system) return 0
  const gridUuids = getGridUuidsFromBlocks(panelBlocks)
  let count = 0
  for (const inverter of system.inverters()) {
    for (const mppt of inverter.mppts()) {
      for (const str of mppt.strings() ?? []) {
        const firstModule = str.modules?.[0]
        if (firstModule && gridUuids.has(firstModule.getGrid()?.uuid)) {
          count++
        }
      }
    }
  }
  return count
}

export function getStrungModuleCountForBlocks(panelBlocks: PanelBlock[], system: StudioSystemType | undefined): number {
  if (!system) return 0
  const gridUuids = getGridUuidsFromBlocks(panelBlocks)
  let count = 0
  for (const inverter of system.inverters()) {
    count += getStrungModuleCountForInverterOnBlocks(inverter, gridUuids)
  }
  return count
}

function getStrungModuleCountForInverterOnBlocks(
  inverter: ReturnType<StudioSystemType['inverters']>[number],
  gridUuids: Set<string>
): number {
  let count = 0
  for (const mppt of inverter.mppts()) {
    for (const str of mppt.strings() ?? []) {
      for (const mod of str.modules ?? []) {
        if (gridUuids.has(mod.getGrid()?.uuid)) {
          count++
        }
      }
    }
  }
  return count
}

export interface MlpeBrandDeviceCount {
  manufacturer: string | null
  quantity: number
}

function addBrandDeviceCount(
  counts: Map<string | null, number>,
  manufacturer: string | null,
  quantity: number
): void {
  if (quantity <= 0) return
  counts.set(manufacturer, (counts.get(manufacturer) ?? 0) + quantity)
}

function resolveMlpeManufacturerName(component: {
  manufacturer_name?: string | null
  getComponentData?: () => unknown
}): string | null {
  if (component.manufacturer_name) return component.manufacturer_name
  const specs = component.getComponentData?.() as { manufacturer_name?: string | null } | undefined
  return specs?.manufacturer_name ?? null
}

type StudioInverterRow = ReturnType<StudioSystemType['inverters']>[number]
type StudioOtherRow = ReturnType<StudioSystemType['others']>[number]

/** Inverters-tab quantity for a microinverter row (synced via getQuantityFromSystemLayout). */
function getMicroInverterRowQuantity(inverter: StudioInverterRow): number {
  if (typeof inverter.getQuantityFromSystemLayout === 'function') {
    const layoutQty = inverter.getQuantityFromSystemLayout()
    if (typeof layoutQty === 'number' && layoutQty > 0) return layoutQty
  }
  const quantity = inverter.quantity
  return typeof quantity === 'number' && quantity > 0 ? quantity : 0
}

/** Inverters-tab quantity for a DC optimizer row (synced via refreshDcOptimizerEfficiencyAndQuantity). */
function getOptimizerRowQuantity(other: StudioOtherRow): number {
  const quantity = other.quantity
  return typeof quantity === 'number' && quantity > 0 ? quantity : 0
}

function gridUuidInScope(gridUuid: string, scopeGridUuids: Set<string> | undefined): boolean {
  return !scopeGridUuids || scopeGridUuids.has(gridUuid)
}

function microInverterHasPresenceOnGrids(
  inverter: StudioInverterRow,
  system: StudioSystemType,
  scopeGridUuids: Set<string> | undefined
): boolean {
  for (const grid of system.moduleGrids?.() ?? []) {
    if (
      gridUuidInScope(grid.uuid, scopeGridUuids) &&
      !grid.dcOptimizerId &&
      (grid.moduleQuantity?.() ?? 0) > 0
    ) {
      return true
    }
  }

  for (const mppt of inverter.mppts()) {
    for (const str of mppt.strings() ?? []) {
      for (const mod of str.modules ?? []) {
        const gridUuid = mod.getGrid()?.uuid
        if (gridUuid && gridUuidInScope(gridUuid, scopeGridUuids)) {
          return true
        }
      }
    }
  }

  return false
}

function optimizerHasPresenceOnGrids(
  other: StudioOtherRow,
  system: StudioSystemType,
  scopeGridUuids: Set<string> | undefined
): boolean {
  for (const grid of system.moduleGrids?.() ?? []) {
    if (
      gridUuidInScope(grid.uuid, scopeGridUuids) &&
      grid.dcOptimizerId === other.other_id &&
      (grid.moduleQuantity?.() ?? 0) > 0
    ) {
      return true
    }
  }
  return false
}

/**
 * Per-brand MLPE device counts from Inverters-tab row quantities, grouped by manufacturer.
 * Block-scoped callers only include brands with modules on the scoped module grids.
 */
function getMlpeBrandDeviceCountsFromInvertersTab(
  system: StudioSystemType,
  scopeGridUuids?: Set<string>,
  options?: { systemWide?: boolean }
): MlpeBrandDeviceCount[] {
  const brandCounts = new Map<string | null, number>()
  const systemWide = options?.systemWide ?? false

  for (const other of system.others()) {
    if (other.other_component_type !== 'dc_optimizer') continue
    if (!systemWide && !optimizerHasPresenceOnGrids(other, system, scopeGridUuids)) continue

    const optimizerQty = getOptimizerRowQuantity(other)
    addBrandDeviceCount(brandCounts, resolveMlpeManufacturerName(other), optimizerQty)
  }

  for (const inverter of system.inverters()) {
    if (!inverter.microinverter) continue
    if (!systemWide && !microInverterHasPresenceOnGrids(inverter, system, scopeGridUuids)) continue

    const microQty = getMicroInverterRowQuantity(inverter)
    addBrandDeviceCount(brandCounts, resolveMlpeManufacturerName(inverter), microQty)
  }

  return [...brandCounts.entries()].map(([manufacturer, quantity]) => ({ manufacturer, quantity }))
}

/**
 * Per-brand MLPE device counts from Inverters-tab quantities for the whole system.
 */
export function getMlpeBrandDeviceCountsForSystem(system: StudioSystemType | undefined): MlpeBrandDeviceCount[] {
  if (!system) return []
  return getMlpeBrandDeviceCountsFromInvertersTab(system, undefined, { systemWide: true })
}

/**
 * Per-brand MLPE device counts from Inverters-tab quantities for brands present on block grids.
 */
export function getMlpeBrandDeviceCountsForBlocks(
  panelBlocks: PanelBlock[],
  system: StudioSystemType | undefined
): MlpeBrandDeviceCount[] {
  if (!system) return []
  return getMlpeBrandDeviceCountsFromInvertersTab(system, getGridUuidsFromBlocks(panelBlocks))
}

/**
 * Block-scoped MLPE device count (sum of per-brand Inverters-tab quantities on block grids).
 */
export function getMlpeQuantityForBlocks(panelBlocks: PanelBlock[], system: StudioSystemType | undefined): number {
  return getMlpeBrandDeviceCountsForBlocks(panelBlocks, system).reduce((sum, line) => sum + line.quantity, 0)
}

export function getMlpeQuantityForSystem(system: StudioSystemType | undefined): number {
  return getMlpeBrandDeviceCountsForSystem(system).reduce((sum, line) => sum + line.quantity, 0)
}

// Counts physical devices mounted on the rail (microinverters + optimizers) for this roof facet.
// Scoped via string hierarchy: only counts modules actually strung to each inverter
// on this roof facet's grids, to avoid overcounting when the system has mixed inverter types.
export function getDeviceCountOnRailForBlocks(panelBlocks: PanelBlock[], system: StudioSystemType | undefined): number {
  if (!system) return 0
  const gridUuids = getGridUuidsFromBlocks(panelBlocks)
  let count = 0

  for (const inverter of system.inverters()) {
    if (inverter.microinverter) {
      const mpptQty = inverter.getComponentData()?.mppt_quantity || 1
      let strungModulesOnFacet = 0
      for (const mppt of inverter.mppts()) {
        for (const str of mppt.strings() ?? []) {
          for (const mod of str.modules ?? []) {
            if (gridUuids.has(mod.getGrid()?.uuid)) {
              strungModulesOnFacet++
            }
          }
        }
      }
      count += Math.ceil(strungModulesOnFacet / mpptQty)
    }
  }

  for (const other of system.others()) {
    if (other.other_component_type === 'dc_optimizer') {
      for (const grid of system.moduleGrids?.() ?? []) {
        if (grid.dcOptimizerId === other.other_id && gridUuids.has(grid.uuid)) {
          count += grid.moduleQuantity?.() ?? 0
        }
      }
    }
  }

  return count
}

/** First MLPE manufacturer with devices on this block (legacy single-SKU callers). */
export function getMlpeInverterManufacturerForBlocks(panelBlocks: PanelBlock[], system: StudioSystemType | undefined): string | null {
  const brandLines = getMlpeBrandDeviceCountsForBlocks(panelBlocks, system)
  return brandLines[0]?.manufacturer ?? null
}

export function hasMlpeOnBlocks(panelBlocks: PanelBlock[], system: StudioSystemType | undefined): boolean {
  return getMlpeQuantityForBlocks(panelBlocks, system) > 0
}
